import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PaymentIcon } from '../../../assets/icons/amount-icon.svg';
import { ReactComponent as RightArrow } from '../../../assets/icons/right-arrow.svg';
import { useAppBranding } from '../../../config/branding';
import config from '../../../config/config';
import { useBranding } from '../../../context/BrandingContext';
import MyButton from '../../../shared-components/button/button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ToastMessage from '../../../shared-components/toast-message/ToastMessage';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import getFirstSubdomain from '../../../shared-functions/DomiansplitFunction';
import FormatTimeTo12HoursFormat from '../../../shared-functions/FormatTime';

import StoreLink from './StoreLink';
import TariffFeesOverview from './TariffFeesOverview';


interface PricingDetailsProperties {
  currentPrice: string;
  dynamicPricing: {
    price: string;
    priceType: string;
    timings: {
      startDateUtc: string | null;
      endDateUtc: string | null;
    }[];
  }[];
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface IdleFeePrices {
  durationAmountList: DurationAmount[];
  ceilingValue: number;
}

export type PricingType = 'FLAT_RATE' | 'PER_MINUTE';

interface PaymentMethodProperties {
  setPaymentDetails: React.Dispatch<React.SetStateAction<any>>;
  navigateToChargingSession: () => void;
  navigateToInvoice?: () => void;
  pricingDetails: PricingDetailsProperties;
  idleFeePrices: IdleFeePrices | null;
  appVersion: string;
  fees?: {
    platformFee: number;
    platformFeeType: string;
    sessionFee: number;
    sessionFeeType: string;
    currencyType?: string;
  } | null;
  isFreeCharging?: boolean;
}

interface PaymentDetailsProperties {
  name: string;
}

/**
 * Payment method component
 * @param properties Component properties
 * @returns React component JSX
 */
function PaymentMethod(properties: Readonly<PaymentMethodProperties>) {
  const { t, i18n } = useTranslation('charger');
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();
  const { images: brandImage } = useAppBranding();

  const {
    navigateToChargingSession,
    setPaymentDetails,
    pricingDetails,
    appVersion,
    fees,
    isFreeCharging: isFreeProperty,
  } = properties;


  const stripe =
    (domain && brandData?.guestPaymentMethod
      ? brandData?.guestPaymentMethod
      : config.guestPaymentMode) === 'stripe'
      ? useStripe()
      : null;
  const elements = useElements();

  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [paymentDetails, setDummyPaymentDetails] = useState<PaymentDetailsProperties>({
    name: '',
  });

  const isFreeCharging =
    isFreeProperty ??
    (fees !== null &&
      fees !== undefined &&
      Number(fees.platformFee ?? 0) === 0 &&
      Number(fees.sessionFee ?? 0) === 0);

  /**
   * Function to handle payment method change
   */
  const handlePaymentDetailsChange = (name: string, changeValue: string | number) => {
    setDummyPaymentDetails((previousValue) => ({
      ...previousValue,
      [name]: changeValue,
    }));
  };

  /**
   * handle payment method
   */
  const handlePaymentMethod = async () => {
    if (
      !isFreeCharging &&
      (domain && brandData?.guestPaymentMethod
        ? brandData?.guestPaymentMethod
        : config.guestPaymentMode) === 'stripe'
    ) {
      setButtonLoading(true);
      if (!stripe || !elements) {
        setButtonLoading(false);
        setToastStatus({
          openToast: true,
          showToastMessage: t('somethingWentWrong'),
        });
        console.error('Stripe has not been properly loaded yet.');
        return;
      }

      if (!paymentDetails.name) {
        setButtonLoading(false);
        setToastStatus({
          openToast: true,
          showToastMessage: t('enterCardHolder'),
        });
        return;
      }

      // Get the card details from the CardElement
      const cardNumber = elements.getElement(CardNumberElement);
      const cardExpireElement = elements.getElement(CardExpiryElement);
      const cardCvc = elements.getElement(CardCvcElement);

      if (!cardNumber || !cardExpireElement || !cardCvc) {
        console.error('CardElement is not loaded yet.');
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber, // Pass the CardNumberElement here
      });

      if (error || !paymentMethod?.id) {
        setButtonLoading(false);
        setToastStatus({
          openToast: true,
          showToastMessage: error?.message || t('invalidCardDetails'),
        });
        return;
      }

      setPaymentDetails({
        paymentMethodId: paymentMethod?.id,
        last4: paymentMethod?.card?.last4,
        expiryMonth: paymentMethod?.card?.exp_month,
        expiryYear: paymentMethod?.card?.exp_year,
        funding: paymentMethod?.card?.funding,
        brand: paymentMethod?.card?.brand,
        cardHolderName: paymentDetails.name,
      });

      navigateToChargingSession();
      setButtonLoading(false);
    } else if (isFreeCharging) {
      navigateToChargingSession();
    } else {
      setToastStatus({
        openToast: true,
        showToastMessage: t('somethingWentWrong'),
      });
    }
  };

  const options = {
    style: {
      base: {
        fontSize: '13px',
        color: '#212121',
        letterSpacing: '0.025em',
        fontFamily: 'Gilroy, sans-serif',
        textAlign: i18n.dir() === 'rtl' ? 'right' : 'left', // 👈 FIX
        direction: i18n.dir(),
        '::placeholder': {
          color: '#6c757d',
          textAlign: i18n.dir() === 'rtl' ? 'right' : 'left',
        },
      },
      invalid: {
        color: '#f04438',
      },
    },
  };

  return (
    <div className="payment-method-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || t('somethingWentWrong')}
        onCloseTimer={6000}
      />
      <div className="payment-method-container-with-warning">
        <div className="payment-method-container">
          <h1>{t('choosePaymentMethod')}</h1>
          <div className="credit-card-warp">
            <img src={brandImage.creditCard} alt="" className="credit-card-image" />
          </div>
          <div className={`payment-method-form ${i18n.dir() === 'rtl' ? 'rtl' : ''}`}>
            <CustomInput
              inputLabel=""
              type="text"
              placeholder={t('cardHolderName')}
              name="name"
              value={paymentDetails.name}
              onChangeValue={handlePaymentDetailsChange}
              valueRequired
            />
            <div className="stripe-element-container">
              <CardNumberElement options={options} />
            </div>
            <div className="field-single-row">
              <div className="stripe-element-container">
                <CardExpiryElement options={options} />
              </div>
              <div className="stripe-element-container">
                <CardCvcElement options={options} />
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Tariff & Fees Overview */}
        <TariffFeesOverview />


        <div className="session-fee-info-text">{t('authorizationHoldNotice')}</div>

        {pricingDetails.dynamicPricing.length > 0 ? (
          <div className="pricing-details">
            {pricingDetails.dynamicPricing.map((pricing) =>
              pricing.timings
                .filter((timing) => timing.startDateUtc !== null && timing.endDateUtc !== null)
                .map((timing) => (
                  <div
                    key={`${pricing.priceType}-${timing.startDateUtc}`}
                    className="pricing-detail">
                    <div className="pricing-icon">
                      <span>
                        <PaymentIcon />
                      </span>
                    </div>
                    <div className="pricing-content">
                      <p>
                        {FormatTimeTo12HoursFormat(ConvertUTCtoHHMM(timing.startDateUtc || ''))} -{' '}
                        {FormatTimeTo12HoursFormat(ConvertUTCtoHHMM(timing.endDateUtc || ''))}
                      </p>
                      <h6>
                        {Number(pricing.price).toFixed(2)} {t('currency')}
                      </h6>
                    </div>
                  </div>
                )),
            )}
          </div>
        ) : null}
      </div>
      <StoreLink />
      <div className="payment-button">
        <MyButton
          label={isFreeCharging ? (t('skip') || 'Skip') : t('continue')}
          svgIcon={<RightArrow />}
          buttonType="submit"
          onClickFunc={handlePaymentMethod}
          loading={buttonLoading}
          disableButton={buttonLoading}
        />
        <div className="app-version">
          <span>{appVersion}</span>
        </div>
      </div>
    </div>
  );
}

export default PaymentMethod;
